import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

let globalSocket: Socket | null = null;
let registeredUserId: string | null = null;

function getSocket(): Socket | null {
  if (globalSocket?.connected) return globalSocket;

  globalSocket = io(SOCKET_URL, {
    autoConnect: false,
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  });

  globalSocket.on('connect', () => {
    console.log('[FadfaddahSocket] Connected');
  });

  globalSocket.on('disconnect', (reason) => {
    console.log('[FadfaddahSocket] Disconnected:', reason);
  });

  globalSocket.on('connect_error', (err) => {
    console.error('[FadfaddahSocket] Connection error:', err.message);
  });

  return globalSocket;
}

export function useFadfaddahSocket(
  userId: string | null,
  chatroomId: string | null,
  onNewMessage: () => void,
  onPendingUpdate?: () => void,
) {
  const [connected, setConnected] = useState(false);
  const chatroomIdRef = useRef(chatroomId);
  const onNewMessageRef = useRef(onNewMessage);
  const onPendingUpdateRef = useRef(onPendingUpdate);

  useEffect(() => {
    chatroomIdRef.current = chatroomId;
  }, [chatroomId]);

  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
  }, [onNewMessage]);

  useEffect(() => {
    onPendingUpdateRef.current = onPendingUpdate;
  }, [onPendingUpdate]);

  useEffect(() => {
    if (!userId) return;

    const socket = getSocket();
    if (!socket) return;

    const onConnect = () => {
      setConnected(true);
    };
    const onDisconnect = () => {
      setConnected(false);
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    if (!socket.connected) {
      socket.connect();
    }

    // Register with userId and admin role so backend adds us to "fadfaddah_admins" room
    const payload = { userId, role: 'admin' };
    if (socket.connected) {
      if (userId !== registeredUserId) {
        socket.emit('register', payload);
        registeredUserId = userId;
      }
    } else {
      socket.once('connect', () => {
        if (userId !== registeredUserId) {
          socket.emit('register', payload);
          registeredUserId = userId;
        }
      });
    }

    setConnected(socket.connected);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  }, [userId]);

  // Listen for new_message events
  useEffect(() => {
    const socket = globalSocket;
    if (!socket) return;

    const handler = (data: any) => {
      const incomingId = data?.chatroomId || data?.chatroomID || data?.roomId;
      if (incomingId && String(incomingId) === String(chatroomIdRef.current)) {
        onNewMessageRef.current?.();
      }
    };

    socket.on('new_message', handler);
    return () => {
      socket.off('new_message', handler);
    };
  }, []);

  // Listen for new pending fadfaddah events
  useEffect(() => {
    const socket = globalSocket;
    if (!socket) return;

    const handler = () => {
      onPendingUpdateRef.current?.();
    };

    socket.on('new_fadfaddah_pending', handler);
    return () => {
      socket.off('new_fadfaddah_pending', handler);
    };
  }, []);

  return { connected };
}
